import { ReactComponent as AddIcon } from '@assets/svg/add_e_visit.svg';
import { ReactComponent as ClockIcon } from '@assets/svg/e_visit_clock.svg';
import { ReactComponent as DeleteIcon } from '@assets/svg/office_hours_range_delete.svg';
import Checkbox from '@components/checkbox';
import { DateTimePicker, MessageBox, RangePickerDeprecated } from '@components/ui';
import { WCS } from '@components/ui/calendar/calendar-week/week-utils';
import { getShortDateFormat } from '@helpers/utils-helpers';
import getDaysArray from '@lib/utils/get-days-array';
import { addDays, isBefore } from 'date-fns';
import { FieldArray, Form, Formik, getIn } from 'formik';
import moment from 'moment';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { IOfficeHoursWeekRule, IOfficeHoursWeekRuleWithId, TDay } from 'types/e-visits';
import { IFormValues, validationSchema } from './office-hours-edit.schema';
import { OfficeHoursEditSC, RangeSC } from './office-hours-edit.styled';
import { generateDisableTimes, getInitialValues } from './office-hours-edit.util';
import { Button } from '@ui';

const WEEK_STARTS_ON = Number(process.env.REACT_APP_WEEK_STARTS_ON) as TDay;

const CalendarIcon = (
  <div
    style={{
      width: '100%',
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}
  >
    <img
      src={require('@assets/svg/icon-datepicker.svg').default}
      alt=""
      style={{ width: 16, height: 16 }}
    />
  </div>
);

interface OfficeHoursEditProps {
  officeHours?: IOfficeHoursWeekRuleWithId | IOfficeHoursWeekRule;
  expertiseCode: string;
  medicalInstitutionId: string;
  onSubmit: (values: IFormValues) => void;
  loading: boolean;
}

const OfficeHoursEdit: React.FunctionComponent<OfficeHoursEditProps> = ({
  officeHours,
  expertiseCode,
  medicalInstitutionId,
  onSubmit,
  loading,
}) => {
  const initialValues = React.useMemo(
    () => getInitialValues({ officeHours, expertiseCode, medicalInstitutionId }),
    [officeHours, expertiseCode, medicalInstitutionId],
  );

  const weekDays = getDaysArray<TDay>(WEEK_STARTS_ON, 'number');

  return (
    <OfficeHoursEditSC>
      <Formik
        initialValues={initialValues}
        onSubmit={onSubmit}
        validationSchema={validationSchema}
        enableReinitialize
      >
        {({ values, setFieldValue, errors, setValues }) => (
          <Form>
            <div className="content">
              {weekDays.map((d) => (
                <>
                  <div className="day" key={d}>
                    <div className="label">{moment().set('day', d).format('dd')}</div>
                    <div className="pickers">
                      <FieldArray name={`days.${d}`}>
                        {(arrayHelpers) => (
                          <>
                            {values.days[d]?.map((range, index) => {
                              const fieldName = `days.${d}.${index}`;
                              const error = getIn(errors, fieldName)
                                ? getIn(errors, fieldName)
                                : null;
                              return (
                                <RangeSC
                                  key={range.from && range.to ? `${range.from}-${range.to}` : index}
                                  error={error}
                                >
                                  <RangePickerDeprecated
                                    picker="time"
                                    generateConfig="moment"
                                    kind="bubble"
                                    showSecond={false}
                                    value={
                                      values.days[d][index].from
                                        ? [
                                            moment(values.days[d][index]?.from),
                                            moment(values.days[d][index]?.to),
                                          ]
                                        : undefined
                                    }
                                    format="HH:mm"
                                    onChange={(newDates) => {
                                      if (newDates === null || !newDates[0] || !newDates[1]) {
                                        return;
                                      }
                                      setFieldValue(fieldName, {
                                        from: newDates[0].toISOString(),
                                        to: newDates[1].toISOString(),
                                      });
                                    }}
                                    minuteStep={WCS.minSelectInterval}
                                    {...generateDisableTimes(
                                      values.days[d]
                                        .slice(0, index)
                                        .concat(values.days[d].slice(index + 1)),
                                    )}
                                  />
                                  <button
                                    className="delete"
                                    onClick={() => arrayHelpers.remove(index)}
                                    type="button"
                                  >
                                    <DeleteIcon />
                                  </button>
                                </RangeSC>
                              );
                            })}
                            <button
                              className="empty-add"
                              type="button"
                              onClick={() => arrayHelpers.push({})}
                            >
                              <AddIcon width="22" height="22" />
                            </button>
                          </>
                        )}
                      </FieldArray>
                    </div>
                  </div>
                  {errors.days?.[d] === 'officeHoursEdit_overlapping_ranges' ? (
                    <div className="inner">
                      <MessageBox
                        size="small"
                        variant="danger"
                        caption={<FormattedMessage id="officeHoursEdit_overlapping_ranges" />}
                      />
                    </div>
                  ) : null}
                </>
              ))}
              {typeof errors.days === 'string' ? (
                <MessageBox
                  size="small"
                  caption={<FormattedMessage id={errors.days} />}
                  variant="danger"
                  style={{ marginBottom: '32px' }}
                />
              ) : null}

              <div className="validity-form">
                <div className="validity-title">
                  <ClockIcon />
                  <FormattedMessage id="officeHoursEdit_validity" />
                </div>
                <div className="validity-content">
                  <div className="validity-item">
                    <Checkbox
                      name="repeatTo"
                      value={values.validityFrom as any}
                      disabled
                      onChange={(e) => {}}
                      className="small"
                    >
                      <FormattedMessage id="addForm_repeatFrom" />
                    </Checkbox>
                    <div style={{ opacity: values.validityFrom ? 1 : 0.6 }}>
                      <DateTimePicker
                        generateConfig="moment"
                        kind="inline"
                        format={getShortDateFormat()}
                        picker="date"
                        value={values.validFrom ? moment(values.validFrom) : null}
                        onChange={(date) => {
                          setValues({
                            ...values,
                            validFrom: date!.toISOString(),
                            validTo:
                              values.validTo &&
                              isBefore(new Date(values.validTo), new Date(date!.toISOString()))!
                                ? date!.add('days', 1).toISOString()
                                : values.validTo,
                          });
                        }}
                        suffixIcon={CalendarIcon}
                        disabled={!values.validityFrom}
                        disabledDate={(d) => d.isBefore(addDays(Date.now(), 1), 'days')}
                      />
                    </div>
                  </div>
                  <div className="validity-item">
                    <Checkbox
                      name="repeatTo"
                      value={values.validityTo as any}
                      onChange={(e) => {
                        if (!e.target.checked) {
                          setValues({
                            ...values,
                            validityTo: false,
                            validTo: undefined,
                          });
                        } else {
                          setValues({
                            ...values,
                            validityTo: true,
                            validTo: addDays(Date.now(), 1).toISOString(),
                          });
                        }
                      }}
                      className="small"
                    >
                      <FormattedMessage id="addForm_repeatTo" />
                    </Checkbox>
                    <div style={{ opacity: values.validityTo ? 1 : 0.6 }}>
                      <DateTimePicker
                        generateConfig="moment"
                        kind="inline"
                        format={getShortDateFormat()}
                        picker="date"
                        value={values.validTo ? moment(values.validTo) : null}
                        onChange={(date) => setFieldValue('validTo', date?.toISOString())}
                        suffixIcon={CalendarIcon}
                        disabled={!values.validityTo}
                        disabledDate={(d) =>
                          d.isBefore(Date.now(), 'days') ||
                          d.isBefore(new Date(values.validFrom!), 'days')
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <Button type="submit" style={{ marginTop: 32 }} loading={loading}>
              <FormattedMessage id="officeHoursEdit_save" />
            </Button>
          </Form>
        )}
      </Formik>
    </OfficeHoursEditSC>
  );
};

export default React.memo(OfficeHoursEdit);
